import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Check, Download, PackageCheck, Pencil, Printer, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import { usePurchaseOrder, useUpdatePurchaseOrderStatus } from "@/features/purchase/api";
import {
  PO_STATUSES,
  canEditLines,
  canReceive,
  labelFor,
  statusTone,
} from "@/features/purchase/constants";
import { PurchaseOrderPrintDocument } from "@/features/purchase/po-print-document";
import { remainingWeight } from "@/features/purchase/receive-api";
import type { PurchaseOrderStatus } from "@/features/purchase/types";
import { useCompany } from "@/features/settings/api";
import { apiDownload, isApiConfigured } from "@/lib/api";

export function PurchaseOrderDetailPage({ poId }: { poId: string }) {
  return (
    <ErrorBoundary title="PO detail crashed">
      <PurchaseOrderDetailInner poId={poId} />
    </ErrorBoundary>
  );
}

function PurchaseOrderDetailInner({ poId }: { poId: string }) {
  const navigate = useNavigate();
  const { data: po, isLoading, error } = usePurchaseOrder(poId);
  const { data: company } = useCompany();
  const updateStatus = useUpdatePurchaseOrderStatus();

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !po) {
    return (
      <EmptyState
        title="Purchase order not found"
        description={(error as Error)?.message ?? "Unknown PO"}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/purchase">Back to list</Link>
          </Button>
        }
      />
    );
  }

  async function setStatus(status: PurchaseOrderStatus) {
    await updateStatus.mutateAsync({ id: poId, status });
  }

  function printPdf() {
    window.print();
  }

  async function downloadPdf() {
    try {
      await apiDownload(`/pdf/purchase-orders/${poId}`, `${po?.po_number || "po"}.pdf`);
    } catch (e) {
      toast.error((e as Error).message || "PDF download failed");
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 print:max-w-none print:space-y-0">
      <PurchaseOrderPrintDocument po={po} company={company ?? null} />

      <div className="space-y-4 print:hidden">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/purchase">
                <ArrowLeft />
              </Link>
            </Button>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-mono text-lg font-semibold">{po.po_number}</h1>
                <StatusDot tone={statusTone(po.status)}>{labelFor(PO_STATUSES, po.status)}</StatusDot>
              </div>
              <p className="text-xs text-muted-foreground">
                {po.vendor_code} · {po.vendor_name} ·{" "}
                {format(parseISO(po.issue_date), "dd MMM yyyy")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {isApiConfigured() ? (
              <Button type="button" variant="outline" size="sm" onClick={() => void downloadPdf()}>
                <Download />
                Download PDF
              </Button>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={printPdf}>
              <Printer />
              Print
            </Button>
            {canEditLines(po.status) || po.status === "sent" || po.status === "confirmed" ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  void navigate({ to: "/purchase/orders/$poId/edit", params: { poId } })
                }
              >
                <Pencil />
                Edit
              </Button>
            ) : null}
            {canReceive(po.status) ? (
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  void navigate({ to: "/purchase/orders/$poId/receive", params: { poId } })
                }
              >
                <PackageCheck />
                Receive stock
              </Button>
            ) : null}
            {po.status === "draft" ? (
              <Button
                type="button"
                size="sm"
                disabled={updateStatus.isPending}
                onClick={() => void setStatus("sent")}
              >
                <Send />
                Mark sent
              </Button>
            ) : null}
            {po.status === "sent" ? (
              <Button
                type="button"
                size="sm"
                disabled={updateStatus.isPending}
                onClick={() => void setStatus("confirmed")}
              >
                <Check />
                Confirm
              </Button>
            ) : null}
            {po.status === "confirmed" ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={updateStatus.isPending}
                onClick={() => void setStatus("closed")}
              >
                Close
              </Button>
            ) : null}
            {po.status !== "cancelled" && po.status !== "closed" ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="hover:text-destructive"
                disabled={updateStatus.isPending}
                onClick={() => void setStatus("cancelled")}
              >
                <XCircle />
                Cancel
              </Button>
            ) : null}
          </div>
        </header>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-3 text-xs">
            <div className="mb-1 font-semibold">Bill to</div>
            <pre className="whitespace-pre-wrap font-sans text-muted-foreground">
              {po.bill_to_text || "—"}
            </pre>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-xs">
            <div className="mb-1 font-semibold">Ship to</div>
            <pre className="whitespace-pre-wrap font-sans text-muted-foreground">
              {po.ship_to_text || "—"}
            </pre>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>#</TableHead>
                <TableHead>Material</TableHead>
                <TableHead className="text-right">Qty / Wt</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(po.lines ?? []).map((line) => (
                <TableRow key={line.id}>
                  <TableCell className="tabular-nums">{line.line_no}</TableCell>
                  <TableCell>
                    <div className="text-xs font-medium">{line.material_name}</div>
                    <div className="text-2xs text-muted-foreground">
                      {[line.description, line.grade_name, line.shape_label]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(line.taxes ?? []).map((t) => (
                        <Badge key={t.id} variant="outline">
                          {t.tax_type.toUpperCase()} {t.rate}%
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {line.qty ? `${line.qty}` : "—"}
                    {line.weight ? (
                      <div className="text-2xs text-muted-foreground">
                        {line.weight} {line.uom_code || "wt"}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {line.received_weight}
                    {line.is_fully_received ? (
                      <div className="text-2xs text-emerald-600">Full</div>
                    ) : remainingWeight(line) > 0 ? (
                      <div className="text-2xs text-muted-foreground">
                        left {remainingWeight(line)}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {formatMoney(line.unit_rate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{line.gst_slab}%</TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-xs">
                    {formatMoney(line.line_subtotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="ml-auto w-full max-w-xs space-y-1 rounded-lg border border-border bg-card p-3 text-xs">
          <Row label="Subtotal" value={formatMoney(po.subtotal)} />
          <Row label="Discount" value={formatMoney(po.discount)} />
          <Row label="Tax" value={formatMoney(po.tax_total)} />
          <Row label="Adjustment" value={formatMoney(po.adjustment)} />
          <div className="flex justify-between border-t border-border pt-1.5 text-sm font-semibold">
            <span>Grand total</span>
            <span className="tabular-nums">{formatMoney(po.grand_total)}</span>
          </div>
        </div>

        {po.notes || po.terms ? (
          <div className="grid gap-3 md:grid-cols-2 text-xs">
            {po.notes ? (
              <div className="rounded-lg border border-border bg-card p-3">
                <div className="mb-1 font-semibold">Notes</div>
                <p className="text-muted-foreground whitespace-pre-wrap">{po.notes}</p>
              </div>
            ) : null}
            {po.terms ? (
              <div className="rounded-lg border border-border bg-card p-3">
                <div className="mb-1 font-semibold">Terms</div>
                <p className="text-muted-foreground whitespace-pre-wrap">{po.terms}</p>
              </div>
            ) : null}
          </div>
        ) : null}

        {po.status === "confirmed" || po.status === "partial" ? (
          <p className="text-2xs text-muted-foreground">
            Use Receive stock to enter actual batch data (dims, location) and create inventory.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
